import { SessionManager } from "../api/SessionManager";
import { SimpleEventEmitter } from "../utils/EventEmitter";
import type { AuthProvider, Credentials, EventEmitter, TokenStorage, Tokens, User } from "../types";

export interface BaseAuthProviderConfig {
  storage: TokenStorage;
  /** Refresh this many ms before the access token's expiry. Default 30s. */
  refreshSkewMs?: number;
  /** Log out after this many ms of inactivity. Omit to disable idle logout. */
  idleTimeoutMs?: number;
  /** Distinguishes this provider's cross-tab broadcast channel from others on the same origin. */
  channelName?: string;
}

/**
 * Shared plumbing for every concrete provider: token persistence, scheduled
 * and deduplicated refresh, idle timeout, and cross-tab sync all live here
 * exactly once. Subclasses only implement the network calls that are
 * actually specific to their backend (JWT, OAuth2, Keycloak, Auth0, Azure AD).
 */
export abstract class BaseAuthProvider implements AuthProvider {
  readonly events: EventEmitter = new SimpleEventEmitter();
  protected readonly session: SessionManager;
  protected currentUser: User | null = null;
  private initialized = false;

  constructor(config: BaseAuthProviderConfig) {
    this.session = new SessionManager({
      storage: config.storage,
      events: this.events,
      performRefresh: () => this.doRefresh(),
      performLogout: () => this.doLogout(),
      refreshSkewMs: config.refreshSkewMs,
      idleTimeoutMs: config.idleTimeoutMs,
      channelName: config.channelName,
    });

    this.events.on("logout", () => {
      this.setCurrentUser(null);
    });
  }

  /** Perform the provider-specific login network call and return the resulting tokens (+ user, if known). */
  protected abstract doLogin(credentials: Credentials): Promise<{ tokens: Tokens; user?: User | null }>;
  /** Perform the provider-specific refresh network call. */
  protected abstract doRefresh(): Promise<Tokens>;
  /** Fetch the current user from the backend using the stored access token. */
  protected abstract doFetchCurrentUser(): Promise<User | null>;
  /** Best-effort provider-specific logout call (revoke refresh token, end SSO session, etc). */
  protected async doLogout(): Promise<void> {
    /* no-op by default; override where the backend needs an explicit call */
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    const tokens = this.session.getTokens();
    if (!tokens) return;

    try {
      const user = await this.doFetchCurrentUser();
      this.setCurrentUser(user);
      if (this.session.getTokens()?.expiresAt !== undefined) {
        // Re-arm the scheduled refresh now that we're initialized in this tab.
        this.session.setTokens(tokens, { broadcast: false });
      }
      this.session.startIdleWatch();
    } catch {
      // Stored tokens are invalid/expired and could not be refreshed transparently.
      this.session.clearTokens();
      this.setCurrentUser(null);
    }
  }

  async login(credentials: Credentials): Promise<void> {
    const { tokens, user } = await this.doLogin(credentials);
    this.session.setTokens(tokens);
    const resolvedUser = user !== undefined ? user : await this.doFetchCurrentUser();
    this.setCurrentUser(resolvedUser);
    this.session.startIdleWatch();
    if (resolvedUser) this.events.emit("login", { user: resolvedUser });
  }

  async logout(): Promise<void> {
    await this.session.logout("user");
  }

  async refresh(): Promise<void> {
    await this.session.refreshOnce();
  }

  async getAccessToken(): Promise<string | null> {
    return this.session.ensureFreshAccessToken();
  }

  async getCurrentUser(): Promise<User | null> {
    if (this.currentUser) return this.currentUser;
    if (!this.session.getTokens()) return null;
    const user = await this.doFetchCurrentUser();
    this.setCurrentUser(user);
    return user;
  }

  isAuthenticated(): boolean {
    return this.session.getTokens() !== null;
  }

  /** Release timers and listeners. Call from AuthProvider (React component) unmount. */
  dispose(): void {
    this.session.dispose();
  }

  protected setCurrentUser(user: User | null): void {
    const changed = user?.id !== this.currentUser?.id;
    this.currentUser = user;
    if (changed) this.events.emit("userChanged", { user });
  }
}
